// The bold variants below share this so the theme has a single bold weight.
const FONT_WEIGHT_BOLD = 600;

const MONOSPACE_FONT_FAMILY = [
  'Monaco',
  'Menlo',
  'Consolas',
  'monospace',
].join(',');

const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  fontSize: 13,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: FONT_WEIGHT_BOLD,
  h1: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.25,
  },
  h2: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.25,
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.25,
  },
  h4: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.25,
  },
  h5: {
    fontSize: '1.0625rem',
    fontWeight: 500,
    lineHeight: 1.25,
    color: '#0B2920',
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.25,
    color: '#0B2920',
  },
  body1: {
    fontSize: 13,
  },
  // 13px suits the tables and forms the product is mostly made of, and is tight
  // for continuous prose. Assistant answers are the first content that reads.
  reading: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.6,
  },
  // Footnotes under a primary surface. Set in pixels: `caption`'s pxToRem(12)
  // scales to about 11px with the 13px base. Line height matches body1's.
  footnote: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  button: {
    textTransform: 'none',
  },
  label: {
    fontSize: 15,
    lineHeight: '17.58px',
    fontWeight: 400,
    color: '#656565',
  },
  smallTitle: {
    fontWeight: '400',
    fontSize: '13px',
    color: '#646464',
  },
  largeTitle: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: '16px',
  },
  mediumTitle: {
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: '14px',
  },
  code: {
    fontFamily: MONOSPACE_FONT_FAMILY,
    fontSize: 12,
    lineHeight: 1.4,
  },
  description: {
    fontSize: 15,
    lineHeight: '17.58px',
    fontWeight: 400,
    color: '#656565',
    fontStyle: 'italic',
  },
} as const;

declare module '@mui/material/styles' {
  interface TypographyVariants {
    label: React.CSSProperties;
    description: React.CSSProperties;
    smallTitle: React.CSSProperties;
    mediumTitle: React.CSSProperties;
    largeTitle: React.CSSProperties;
    code: React.CSSProperties;
    reading: React.CSSProperties;
    footnote: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
    smallTitle?: React.CSSProperties;
    mediumTitle?: React.CSSProperties;
    largeTitle?: React.CSSProperties;
    description?: React.CSSProperties;
    code?: React.CSSProperties;
    reading?: React.CSSProperties;
    footnote?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label: true;
    smallTitle: true;
    mediumTitle: true;
    largeTitle: true;
    description: true;
    code: true;
    reading: true;
    footnote: true;
  }
}

export default typography;
